import { useCallback, useEffect, useRef, useState } from 'react';

/** Delay before announcing to ensure screen readers detect changes */
export const ANNOUNCE_ERRORS_TIMEOUT_MS = 100;

/**
 * A reusable hook for announcing messages to screen readers using ARIA live regions.
 * Clears previous announcements and adds a small delay to ensure detection.
 *
 * Components should handle translation of messages before passing to announce().
 *
 * @returns An object containing:
 *   - announcement: The current announcement string to render in an ARIA live region
 *   - announce: Function to announce a single message (pre-translated)
 *
 * @example
 * ```tsx
 * const { announcement, announce } = useAriaAnnouncement();
 *
 * // Announce a single message (translate before calling)
 * announce(_t('Form submitted successfully'));
 *
 * // Announce validation errors (collect, format, translate, then announce)
 * const errors = [emailError, passwordError].filter(Boolean);
 * if (errors.length > 0) {
 *   const errorList = errors.join('. ');
 *   announce(_t('Validation errors: #{errorList}', { errorList }));
 * }
 *
 * // Render the announcement in JSX
 * <VisuallyHidden>
 *   <div role="alert" aria-live="assertive" aria-atomic="true">
 *     {announcement}
 *   </div>
 * </VisuallyHidden>
 * ```
 *
 * @see WCAG 4.1.3 Status Messages
 * @see https://www.w3.org/WAI/WCAG22/Understanding/status-messages.html
 */
export const useAriaAnnouncement = () => {
  const [announcement, setAnnouncement] = useState('');
  const timeoutRef = useRef<number>();

  /**
   * Announces a message to screen readers.
   * Clears previous announcements and adds a small delay to ensure detection.
   * Message should be pre-translated by the caller.
   *
   * @param message - The message to announce (should already be translated)
   */
  const announce = useCallback<(message: string) => void>((message: string) => {
    setAnnouncement('');
    window.clearTimeout(timeoutRef.current);
    timeoutRef.current = window.setTimeout(() => setAnnouncement(message), ANNOUNCE_ERRORS_TIMEOUT_MS);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => () => window.clearTimeout(timeoutRef.current), []);

  return { announcement, announce };
};

export default useAriaAnnouncement;
